/**
 * The operator's decision controls.
 *
 * Three rules shape this component:
 *
 * 1. Nothing is applied locally. The status on screen only ever changes because
 *    REST said so — an optimistic flip would show a decision the server may
 *    have refused on a concurrency conflict.
 * 2. One click is one INTENT, and an intent owns an idempotency key for its
 *    whole life. Retrying a failed submission reuses that key so the server
 *    replays instead of applying the action twice; starting a different action
 *    mints a new one.
 * 3. Only the fields belonging to the chosen command are sent.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2, MessageSquarePlus, PencilLine, RotateCcw, X } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/ui/select";
import { cn } from "@/ui/lib/utils";
import {
  CLOSE_DISPOSITIONS,
  DEFAULT_CLOSE_DISPOSITION,
} from "@/alerts/utils/dispositions";
import { alertWorkflowFailure, alertWorkflowFailureMessage, AlertApiError } from "@/alerts/api/envelope";
import { AlertStatus } from "@/alerts/types/machine-alert";
import type { ReopenReason } from "@/alerts/types/alerts-workspace";
import { useAlertAction } from "@/alerts/api/queries/alerts-workspace.queries";
import type {
  AlertActionCommand,
  AlertActionRequest,
  CloseDispositionWrite,
} from "@/alerts/types/alerts-workspace";

/** A distinct user intent, keyed for its whole retryable life. */
interface PendingIntent {
  command: AlertActionCommand;
  idempotencyKey: string;
}

const newIntent = (command: AlertActionCommand): PendingIntent => ({
  command,
  idempotencyKey: crypto.randomUUID(),
});

/**
 * Reopen reasons, in the order an operator is likely to want them. Reopening
 * starts a new review cycle, so the reason is part of the audit trail, not a
 * formality. `code` is the wire value; only `labelKey` is translated.
 */
const REOPEN_REASONS: {
  code: ReopenReason;
  labelKey:
    | "reopenReasons.newEvidence"
    | "reopenReasons.decisionDisputed"
    | "reopenReasons.contextChanged"
    | "reopenReasons.other";
}[] = [
  { code: "new_evidence", labelKey: "reopenReasons.newEvidence" },
  { code: "decision_disputed", labelKey: "reopenReasons.decisionDisputed" },
  { code: "context_changed", labelKey: "reopenReasons.contextChanged" },
  { code: "other", labelKey: "reopenReasons.other" },
];

export const AlertActionBar = ({
  alertId,
  workflowVersion,
  correctionTarget,
  currentStatus = null,
  disabled,
  onActionApplied,
}: {
  alertId: number;
  /** From canonical detail. Never rendered — it is a concurrency token. */
  workflowVersion: number | null;
  /**
   * What "Correct" is correcting TO, derived from the alert's current result by
   * the caller (which holds canonical detail). Defaults to confirming, the
   * common case: most corrections rescue a wrongly-closed alert.
   */
  correctionTarget?: "confirmed_signal" | "closed";
  /**
   * Canonical status. A first decision and a correction are DIFFERENT commands
   * and the server rejects the wrong one — `confirm_signal` on an already
   * decided alert is a 422, which is what pressing Confirm there used to do.
   * Null while detail loads: the decision buttons stay hidden rather than
   * guessing which kind of action this alert takes.
   */
  currentStatus?: AlertStatus | null;
  /** True while detail is still loading: acting on an unknown version is unsafe. */
  disabled?: boolean;
  /** Fired after REST confirms a state change, so the pane can advance. */
  onActionApplied?: () => void;
}) => {
  const { t } = useTranslation("alerts");
  const action = useAlertAction();
  const [disposition, setDisposition] = useState<CloseDispositionWrite>(DEFAULT_CLOSE_DISPOSITION);
  const [reopenReason, setReopenReason] = useState<ReopenReason>("new_evidence");
  const [note, setNote] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  // Held across a failure so "try again" is the SAME intent, not a new one.
  const [intent, setIntent] = useState<PendingIntent | null>(null);

  const submit = (command: AlertActionCommand, build: () => AlertActionRequest) => {
    // Reuse the key only when retrying the identical command; any other button
    // is a new decision and must not replay the previous one's result.
    const current = intent?.command === command ? intent : newIntent(command);
    setIntent(current);
    action.mutate(
      {
        alertId,
        action: build(),
        workflowVersion,
        idempotencyKey: current.idempotencyKey,
      },
      {
        onSuccess: () => {
          setIntent(null);
          setNote("");
          setNoteOpen(false);
          onActionApplied?.();
        },
      },
    );
  };

  const busy = action.isPending;
  const blocked = disabled || busy;
  // Decided → only a correction is valid. Unknown status → offer neither.
  const decided = currentStatus != null && currentStatus !== AlertStatus.Pending;
  const undecided = currentStatus === AlertStatus.Pending;
  // The reason selector belongs to whichever action carries a disposition.
  const showDisposition = undecided || (decided && correctionTarget === "closed");
  const noteSoFar = note.trim();
  // `other` makes the note the reason, so an empty one leaves the new review
  // cycle unexplained — the contract requires it and so does the button.
  const reopenNeedsNote = reopenReason === "other" && !noteSoFar;
  const trimmedNote = note.trim();

  const failure = action.error ? alertWorkflowFailure(action.error) : null;
  const errorMessage = failure
    ? alertWorkflowFailureMessage(
        failure,
        action.error instanceof AlertApiError ? action.error.message : "",
      )
    : null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Confirm is the primary positive decision — a FIRST one. Hidden once
            the alert is decided, where the valid command is a correction. */}
        {undecided && (
        <Button
          size="sm"
          className="h-8 gap-1.5 text-xs"
          disabled={blocked}
          onClick={() => submit("confirm_signal", () => ({ command: "confirm_signal" }))}
        >
          {busy && intent?.command === "confirm_signal" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          {t("actionBar.confirm")}
        </Button>
        )}

        {/* Dismiss carries a disposition, defaulted so the quick path is one
            click; the selector is there for the operator who wants to be
            specific. A note stays optional and never blocks the decision. */}
        {undecided && (
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
          disabled={blocked}
          onClick={() =>
            submit("close_alert", () => ({
              command: "close_alert",
              disposition,
              note: trimmedNote || undefined,
            }))
          }
        >
          {busy && intent?.command === "close_alert" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <X className="h-3.5 w-3.5" />
          )}
          {t("actionBar.dismiss")}
        </Button>
        )}

        {showDisposition && (
        <Select
          value={disposition}
          disabled={blocked}
          onValueChange={(value) => setDisposition(value as CloseDispositionWrite)}
        >
          <SelectTrigger className="h-8 w-[168px] text-xs" aria-label={t("actionBar.dispositionLabel")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CLOSE_DISPOSITIONS.map((option) => (
              <SelectItem key={option.code} value={option.code} className="text-xs">
                <span title={t(option.helpKey)}>{t(option.labelKey)}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}

        {decided && (
          <>
            <Select
              value={reopenReason}
              disabled={blocked}
              onValueChange={(value) => setReopenReason(value as ReopenReason)}
            >
              <SelectTrigger
                className="h-8 w-[168px] text-xs"
                aria-label={t("actionBar.reopenReasonLabel")}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REOPEN_REASONS.map((option) => (
                  <SelectItem key={option.code} value={option.code} className="text-xs">
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 text-xs"
              disabled={blocked || reopenNeedsNote}
              title={reopenNeedsNote ? t("actionBar.reopenNeedsNote") : undefined}
              onClick={() =>
                submit("reopen", () =>
                  reopenReason === "other"
                    ? { command: "reopen", reason_code: "other", note: trimmedNote }
                    : { command: "reopen", reason_code: reopenReason, note: trimmedNote || undefined },
                )
              }
            >
              {busy && intent?.command === "reopen" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" />
              )}
              {t("actionBar.reopen")}
            </Button>
          </>
        )}

        {decided && (
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 text-xs"
          disabled={blocked}
          onClick={() =>
            submit("correct_result", () =>
              // The target is what makes this a correction rather than a bare
              // "something changed"; without it the server cannot know which way
              // to flip the decision, and the action did nothing (MIN-270).
              correctionTarget === "closed"
                ? {
                    command: "correct_result",
                    target_result: "closed",
                    disposition,
                    note: trimmedNote || undefined,
                  }
                : {
                    command: "correct_result",
                    target_result: "confirmed_signal",
                    note: trimmedNote || undefined,
                  },
            )
          }
        >
          <PencilLine className="h-3.5 w-3.5" />
          {correctionTarget === "closed"
            ? t("actionBar.changeToFalse")
            : t("actionBar.changeToResolved")}
        </Button>
        )}

        <Button
          size="sm"
          variant="ghost"
          className="h-8 gap-1.5 text-xs"
          aria-expanded={noteOpen}
          onClick={() => setNoteOpen((open) => !open)}
        >
          <MessageSquarePlus className="h-3.5 w-3.5" />
          {t("actionBar.note")}
        </Button>
      </div>

      {noteOpen && (
        <div className="space-y-1.5">
          <label htmlFor="alert-note" className="sr-only">
            {t("actionBar.note")}
          </label>
          <textarea
            id="alert-note"
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t("actionBar.notePlaceholder")}
            className="w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-7 text-xs"
              // A note IS its content — an empty one records nothing. Every other
              // action treats the note as optional and is never blocked by it.
              disabled={blocked || trimmedNote.length === 0}
              onClick={() => submit("add_note", () => ({ command: "add_note", note: trimmedNote }))}
            >
              {t("actionBar.saveNote")}
            </Button>
            {trimmedNote.length === 0 && (
              <span className="text-[11px] text-muted-foreground">
                {t("actionBar.noteRequired")}
              </span>
            )}
          </div>
        </div>
      )}

      {errorMessage && (
        <p
          role="alert"
          className={cn(
            "rounded-md border px-2 py-1.5 text-[11px]",
            "border-destructive/30 bg-destructive/10 text-destructive",
          )}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};
